import { StyleSheet } from 'react-native';

/** Onboarding-design tokens (dark hero + purple accent), shared by every step. */
export const C = {
  bg: '#0F0A1A',
  accent: '#A78BFA',
  accentInk: '#111827',
  ink: '#F7FAFC',
  muted: '#9CA3AF',
  bubble: '#1F2937',
  border: '#374151',
  good: '#6EE7B7',
  bad: '#F87171',
} as const;

export const onboardingStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: C.bg,
  },
  safe: {
    flex: 1,
    zIndex: 1,
  },
  // Top row: back button (+ optional progress bar)
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 4,
    minHeight: 48,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backGlyph: {
    color: C.ink,
    fontSize: 26,
    lineHeight: 26,
  },
  progressTrack: {
    flex: 1,
    height: 8,
    borderRadius: 9999,
    backgroundColor: C.bubble,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 9999,
    backgroundColor: C.accent,
  },
  // Centred intro layout (mascot + speech bubble)
  introBody: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 30,
    gap: 14,
  },
  speechBubble: {
    backgroundColor: C.bubble,
    borderColor: C.border,
    borderWidth: 1,
    borderRadius: 24,
    paddingVertical: 18,
    paddingHorizontal: 24,
    maxWidth: 320,
  },
  speechText: {
    color: C.ink,
    fontSize: 22,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 30,
  },
  introMascot: {
    marginTop: 6,
  },
  // Question layout (mascot + small bubble at top, then content)
  questionHeader: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  smallBubble: {
    flex: 1,
    backgroundColor: C.bubble,
    borderColor: C.border,
    borderWidth: 1,
    borderRadius: 18,
    borderBottomLeftRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  smallBubbleText: {
    color: C.ink,
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 21,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 22,
  },
  // Age step: the date-of-birth card scrolls so the keyboard never hides Continue.
  ageScroll: {
    flex: 1,
  },
  ageScrollContent: {
    paddingHorizontal: 12,
    paddingTop: 22,
    paddingBottom: 24,
  },
  // Auto-height header for the leagues step: heading + subheading + search sit directly
  // above the scrollable list (no flex, so the list is not shoved to the bottom).
  leaguesHeader: {
    paddingHorizontal: 24,
    paddingTop: 18,
  },
  searchInput: {
    marginTop: 14,
    backgroundColor: C.bubble,
    borderColor: C.border,
    borderWidth: 1.5,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    color: C.ink,
    fontSize: 15,
    fontWeight: '600',
  },
  heading: {
    color: C.ink,
    fontSize: 26,
    fontWeight: '700',
  },
  subheading: {
    color: C.muted,
    fontSize: 14,
    marginTop: 6,
  },
  // Username input
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
    backgroundColor: C.bubble,
    borderColor: C.border,
    borderWidth: 1.5,
    borderRadius: 12,
    paddingLeft: 16,
    paddingRight: 6,
  },
  input: {
    flex: 1,
    color: C.ink,
    fontSize: 17,
    fontWeight: '600',
    paddingVertical: 15,
  },
  checkButton: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkButtonText: {
    fontSize: 13,
    fontWeight: '700',
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 12,
  },
  hintText: {
    color: C.muted,
    fontSize: 14,
    marginTop: 12,
  },
  // Leagues grid
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 12,
    borderWidth: 1.5,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 58,
  },
  chipBadge: {
    width: 38,
    height: 38,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2B2140',
  },
  chipBadgeText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 13,
  },
  chipLabel: {
    flex: 1,
    color: C.ink,
    fontSize: 13,
    fontWeight: '600',
  },
  // Leagues: 2-column grid of compact tiles (flag on top · name below · check in corner).
  leagueGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 12,
  },
  leagueTile: {
    width: '48%',
    minHeight: 104,
    borderRadius: 14,
    borderWidth: 1.5,
    paddingVertical: 16,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  leagueTileName: {
    color: C.ink,
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  leagueTileCheck: {
    position: 'absolute',
    top: 8,
    right: 10,
    color: C.accent,
    fontWeight: '800',
    fontSize: 16,
  },
  leagueFlagSlot: {
    width: 44,
    height: 32,
    borderRadius: 6,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2B2140',
  },
  leagueFlagImage: {
    width: 44,
    height: 32,
  },
  leagueFlagInitials: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
  // Done summary
  doneBody: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 30,
    gap: 6,
  },
  doneTitle: {
    color: C.ink,
    fontSize: 26,
    fontWeight: '800',
    textAlign: 'center',
  },
  doneSub: {
    color: C.muted,
    fontSize: 16,
    textAlign: 'center',
    marginTop: 4,
  },
  summaryCard: {
    width: '100%',
    backgroundColor: C.bubble,
    borderRadius: 16,
    padding: 18,
    marginTop: 24,
    gap: 14,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  summaryLabel: {
    color: C.muted,
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  summaryValue: {
    color: C.ink,
    fontSize: 15,
    fontWeight: '700',
  },
  summaryDivider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  // Footer CTAs
  footer: {
    paddingHorizontal: 22,
    paddingBottom: 28,
    paddingTop: 8,
  },
  primaryButton: {
    minHeight: 54,
    borderRadius: 12,
    backgroundColor: C.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.45,
  },
  primaryButtonText: {
    color: C.accentInk,
    fontSize: 16,
    fontWeight: '700',
  },
  ghostButton: {
    minHeight: 54,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: C.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ghostButtonText: {
    color: C.muted,
    fontSize: 16,
    fontWeight: '600',
  },
  pressed: {
    transform: [{ scale: 0.97 }],
  },
});
